import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CalendarDays, Clock, MapPin, ArrowRight, Bed, Bath, Move } from "lucide-react";
import { api, fmtPrice, fmtBaths } from "../lib/api";
import Nav from "../components/site/Nav";
import Footer from "../components/site/Footer";

const fmtDate = (d) => {
  if (!d) return "";
  return new Date(`${d}T12:00:00`).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
};

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return m ? `${hr}:${String(m).padStart(2, "0")} ${ampm}` : `${hr} ${ampm}`;
};

export default function OpenHouses() {
  const [events, setEvents] = useState(null);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/open-houses").then((r) => setEvents(r.data)).catch(() => setEvents([]));
    api.get("/profile").then((r) => setProfile(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const contactMabel = () => {
    navigate("/");
    let tries = 0;
    const timer = setInterval(() => {
      const el = document.getElementById("contact");
      if (!el) { if (++tries > 30) clearInterval(timer); return; }
      const r = el.getBoundingClientRect();
      if (r.top >= -80 && r.top < window.innerHeight * 0.5) { clearInterval(timer); return; }
      el.scrollIntoView({ behavior: "auto", block: "start" });
      if (++tries > 30) clearInterval(timer);
    }, 300);
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = (events || []).filter((e) => !e.date || e.date >= today);
  const past = (events || []).filter((e) => e.date && e.date < today);

  return (
    <div className="bg-[#FAF9F6] min-h-screen" data-testid="open-houses-page">
      <Nav name={profile?.name} logo={profile?.logo} />

      <section className="pt-36 pb-16 px-5 md:px-8 max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--gold)] mb-4">You're Invited</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0A1128]">Open Houses</h1>
        <p className="text-base text-[var(--slate)] mt-5 max-w-2xl leading-relaxed">
          Tour Mabel's listings in person. No appointment needed — just drop by during the times below,
          or reach out to arrange a private showing.
        </p>
      </section>

      <section className="px-5 md:px-8 pb-24 max-w-7xl mx-auto">
        {events === null ? (
          <div className="h-40" />
        ) : upcoming.length === 0 ? (
          <div data-testid="open-houses-empty" className="border border-dashed border-[#0A1128]/15 bg-white p-16 text-center">
            <CalendarDays size={32} className="mx-auto text-[var(--gold)] mb-4" />
            <p className="font-serif text-2xl text-[#0A1128]">No open houses scheduled right now</p>
            <p className="text-sm text-[var(--slate)] mt-3">Check back soon — or contact Mabel to arrange a private viewing.</p>
            <button
              data-testid="oh-contact-empty"
              onClick={contactMabel}
              className="mt-8 px-8 py-3 bg-[#0A1128] text-[#FAF9F6] text-[13px] uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300"
            >
              Contact Mabel
            </button>
          </div>
        ) : (
          <div className="space-y-10">
            {upcoming.map((e) => {
              const p = e.property || {};
              return (
                <article
                  key={e.id}
                  data-testid={`open-house-card-${e.id}`}
                  className="bg-white border border-[#0A1128]/10 grid md:grid-cols-[2fr_3fr] group"
                >
                  <div className="relative overflow-hidden min-h-64">
                    {p.images?.[0] ? (
                      <img
                        src={p.images[0]}
                        alt={p.title}
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-[#0A1128] grid place-items-center">
                        <div className="text-center px-8">
                          <CalendarDays size={36} className="mx-auto text-[var(--gold)] mb-4" />
                          <p className="font-serif text-2xl text-[#FAF9F6]">Details Coming Soon</p>
                          <p className="text-[#FAF9F6]/60 text-sm mt-2">Property to be announced</p>
                        </div>
                      </div>
                    )}
                    <div className="absolute top-5 left-5 bg-[var(--gold)] text-[#0A1128] px-4 py-2 text-xs uppercase tracking-[0.18em]">
                      Open House
                    </div>
                  </div>
                  <div className="p-8 md:p-10 flex flex-col">
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[var(--gold)]">
                      <span className="flex items-center gap-2 text-sm uppercase tracking-wider">
                        <CalendarDays size={16} /> {fmtDate(e.date)}
                      </span>
                      <span className="flex items-center gap-2 text-sm uppercase tracking-wider">
                        <Clock size={16} /> {fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ""}
                      </span>
                    </div>
                    <h2 className="font-serif text-2xl md:text-3xl text-[#0A1128] mt-4">{p.title || "Property To Be Announced"}</h2>
                    {p.location && (
                      <p className="flex items-center gap-2 text-sm text-[var(--slate)] mt-2">
                        <MapPin size={15} /> {p.address || p.location}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-4 text-sm text-[#0A1128]">
                      {typeof p.price === "number" && p.price > 0 && (
                        <span className="font-serif text-xl text-[var(--gold)]">{fmtPrice(p.price, p.currency)}</span>
                      )}
                      {p.beds ? <span className="flex items-center gap-1.5"><Bed size={15} /> {p.beds} Beds</span> : null}
                      {p.baths ? <span className="flex items-center gap-1.5"><Bath size={15} /> {fmtBaths(p.baths)} Baths</span> : null}
                      {p.area ? <span className="flex items-center gap-1.5"><Move size={15} /> {p.area.toLocaleString()} sqft</span> : null}
                    </div>
                    {e.note && <p className="text-sm text-[var(--slate)] leading-relaxed mt-4">{e.note}</p>}
                    <div className="flex flex-wrap gap-3 pt-6 border-t border-[#0A1128]/10 mt-auto">
                      {p.id || e.property_id ? (
                        <Link
                          to={`/property/${e.property_id}`}
                          data-testid={`oh-view-property-${e.id}`}
                          className="flex items-center gap-2 px-6 py-3 bg-[#0A1128] text-[#FAF9F6] text-[13px] uppercase tracking-[0.14em] hover:bg-[#1a2342] transition-colors duration-300"
                        >
                          View Property <ArrowRight size={15} />
                        </Link>
                      ) : null}
                      <button
                        data-testid={`oh-contact-${e.id}`}
                        onClick={contactMabel}
                        className="px-6 py-3 border border-[#0A1128]/25 text-[#0A1128] text-[13px] uppercase tracking-[0.14em] hover:bg-[#E8E4D9]/40 transition-colors duration-300"
                      >
                        Contact Mabel
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {past.length > 0 && (
          <div className="mt-16">
            <h2 className="font-serif text-2xl text-[#0A1128]/50 mb-6">Past Open Houses</h2>
            <div className="space-y-3">
              {past.map((e) => (
                <div key={e.id} className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-[var(--slate)]/70 bg-white/60 border border-[#0A1128]/5 px-6 py-4">
                  <span className="uppercase tracking-wider">{fmtDate(e.date)}</span>
                  <span>{fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ""}</span>
                  <span className="font-medium">{e.property?.title}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer profile={profile} />
    </div>
  );
}
